import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import cx from 'classnames';
import { getFlyingBetslipConfig } from 'helpers/prematch';

import {
  createSportsbookState,
  SiteConfigManager,
  SportContext,
  SportManager,
  SportsbookProvider,
  useApplicationState,
  usePreferences,
  useSportsbookState,
} from '@atlas/core';
import { Label, Loader } from '@atlas/ui';

import { sportService } from 'common/constants';
import { groupBy } from 'common/utils/groupBy';
import { createURL } from 'src/helpers/url';

import FavoriteSport from './FavoriteSport';
import SimplifiedFavoriteSport from './SimplifiedFavoriteSport';

const { LIVE, PREMATCH } = sportService;

// sportsbook wrapper required for
// 1 - events from prematch and live should be available
// 2 - sports from prematch and live should be available
const FavoritesSportsbook = () => {
  const sportsbookState = createSportsbookState({
    id: 'favorite',
    sportServices: [LIVE, PREMATCH],
  });

  return (
    <SportsbookProvider value={sportsbookState}>
      <SportsbookFavorites />
    </SportsbookProvider>
  );
};

const SportsbookFavorites = () => {
  const applicationState = useApplicationState();
  const { authenticated, resolved } = applicationState;
  const { initialized, sports } = useSportsbookState();
  const { favoriteEvents, loadingState, setFavoriteEvents } = usePreferences();
  const [loadingStatus, setLoadingStatus] = React.useState('initial'); // initial | inProgress | success | error
  const [sportStructure, setSportStructure] = useState([]);
  const [favoriteEventIds, setFavoriteEventIds] = useState(undefined);

  useEffect(() => {
    if (favoriteEventIds) {
      return;
    }

    if (loadingState === 'success') {
      setFavoriteEventIds(favoriteEvents);
    }
  }, [favoriteEventIds, loadingState, favoriteEvents]);

  const mainMarketsChoice = !!SiteConfigManager.getConfig(
    'config.config.sportsbook.mainMarketsChoice',
  );
  const showMultilines = !!SiteConfigManager.getConfig(
    'config.config.sportsbook.showMobileMultilines',
  );

  const getSelectedSport = useCallback(id => sports.find(sport => sport.id === id), [sports]);

  const {
    location: { pathname },
    push: historyPush,
  } = useHistory();

  useEffect(() => {
    if (loadingStatus !== 'initial' || !favoriteEventIds || !initialized) {
      return;
    }

    if (favoriteEventIds && favoriteEventIds.length === 0) {
      if (loadingState === 'success') {
        setLoadingStatus('success');
      }
      return;
    }

    setLoadingStatus('inProgress');

    SportManager.findEvents({
      filter: {
        eventIds: favoriteEventIds,
        sportServices: [LIVE, PREMATCH],
      },
    })
      .then(({ data: tournaments }) => {
        SportManager.findTournaments({
          filter: { tournamentIds: Object.values(tournaments).map(value => value.tournamentId) },
        })
          .then(({ data: tournamentsData }) => {
            const loadedEventIds = [];
            const sportStructure = Object.values(
              groupBy(
                Object.values(tournaments).map(tournament => {
                  const { events } = tournament;
                  const arrEvents = Object.values(events).sort((a, b) => a.position - b.position);
                  // Adding missing tournament info
                  const id = arrEvents[0].tournamentId;
                  const sportId = arrEvents[0].sportId;
                  const categoryId = arrEvents[0].categoryId;
                  const name = arrEvents[0].tournamentName;
                  const categoryName = arrEvents[0].categoryName;
                  const sportName = arrEvents[0].sportName;
                  const imagePaths = tournamentsData.find(value => value.id === id).imagePaths;
                  const tournamentData = tournamentsData.find(value => value.id === id);

                  arrEvents.forEach(({ id }) => loadedEventIds.push(id));

                  return {
                    categoryId,
                    categoryName,
                    events: arrEvents,
                    id,
                    imagePaths,
                    name,
                    sportFormMainTypes: tournamentData && tournamentData.sportForm,
                    sportId,
                    sportName,
                  };
                }),
                'sportId',
              ),
            )
              .map(tournaments => {
                const id = tournaments[0].sportId;
                const name = tournaments[0].sportName;
                const position = sports.find(sport => sport.id === id).position;
                const amountOfEvents = tournaments.reduce((sum, tournament) => {
                  const amountOfEvents = tournament.events.length;
                  return sum + amountOfEvents;
                }, 0);

                return {
                  amountOfEvents,
                  id,
                  name,
                  position,
                  tournaments,
                };
              })
              .sort((a, b) => a.position - b.position);

            // clean up all unavailable event ids from favorites
            if (favoriteEventIds.length !== loadedEventIds.length) {
              setFavoriteEvents(loadedEventIds);
            }

            setSportStructure(sportStructure);
          })
          .then(() => setLoadingStatus('success'))
          .catch(() => setLoadingStatus('error'));
      })
      .catch(() => setLoadingStatus('error'));
  }, [favoriteEventIds, initialized, loadingState, loadingStatus, setFavoriteEvents, sports]);

  const layoutConfig = useMemo(() => {
    const mobileGridViewType = SiteConfigManager.getConfig(
      'config.config.sportsbook.mobileGridViewType',
    );
    const isSimplifiedGrid = mobileGridViewType === 'simplified';

    return {
      cls: cx('category-container', 'categories', {
        'layout-to-scroll-with-padding': isSimplifiedGrid && getFlyingBetslipConfig().hasExtended,
      }),
      isSimplifiedGrid,
    };
  }, []);

  if (resolved && !authenticated) {
    const url = createURL(pathname.split('/')[1], { cmd: 'signin' });

    historyPush(url.pathname + url.search);
  }

  if (['initial', 'inProgress'].includes(loadingStatus) || !resolved || !initialized) {
    return (
      <div className="empty-state-wrapper favorite-page">
        <Loader className="absolute" />
      </div>
    );
  }

  if (
    loadingStatus === 'success' &&
    (favoriteEventIds.length === 0 || sportStructure.length === 0)
  ) {
    return (
      <div className="empty-state-wrapper">
        <div className="empty-state-text">
          <Label className="title-text" message="you_have_no_favourite_events" />
          <Label
            className="sub-title-text"
            message="to_add_a_match_to_your_favorites_click_on_the_star_icon"
          />
        </div>
      </div>
    );
  }

  return (
    <div className={layoutConfig.cls}>
      {sportStructure.map(({ amountOfEvents, id, name, tournaments }) => (
        <SportContext.Provider key={id} value={getSelectedSport(id)}>
          {layoutConfig.isSimplifiedGrid ? (
            <SimplifiedFavoriteSport name={name} tournaments={tournaments} />
          ) : (
            <FavoriteSport
              amountOfEvents={amountOfEvents}
              mainMarketsChoice={mainMarketsChoice}
              name={name}
              showMultilines={showMultilines}
              tournaments={tournaments}
            />
          )}
        </SportContext.Provider>
      ))}
    </div>
  );
};

export default FavoritesSportsbook;
